import { IAMPConfigs } from "../models/IAMPSettings";
import { ICategoryOrTag, ICommonCategoryMeta, IMostViewedPost, IPost, IPostFilter, IResolvePathResponse, ISpecialCategory } from "../models/ICategory";
import { IPaginatedData } from "../models/ICommon";
import { IGeneralSettings } from "../models/IGeneralSettings";
import { ISection } from "../models/ISection";
import { IMenuItem } from "../models/IMenuItem";
import { INewsFilter } from "../models/INewsMeta";
import { IStaticPage } from "../models/IStaticPage";
import { httpService } from "./HttpService";
import { IAuthor } from "../models/IAuthor";
import { appConfig } from '../config';
import { ISocialFeed } from '../models/ISocialFeed';
import { IBanner } from '../models/IBanner';
import { IStory } from '../models/IStory';
import { INewsLetterSubscription } from '../models/IINewsLetter';
import { IPoll, IPollsCollection } from '../models/IPoll';
import { httpServiceWithFetch } from './HttpServiceWithFetch';
class ApiService {
  private apiBaseUrl = appConfig.NEXT_PUBLIC_API_BASE_URL;

  // dependency injection for unit testing
  constructor(protected client = httpService) { }

  getStaticPage(key: string) {
    return this.client.getJSON<IStaticPage>(`${this.apiBaseUrl}/api/StaticPages/${key}`, { next: { revalidate: 900 } });
  }

  getMenu() {
    return this.client.getJSON<IMenuItem[]>(`${this.apiBaseUrl}/api/Home/menus`);
  }

  getHomeSections(paging: { page: number; pageSize: number; } = { page: 1, pageSize: 10 }) {
    return this.client.getJSON<IPaginatedData<ISection>>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/Home/sections`, paging), { next: { revalidate: 300 } });
  }


  getAMPConfigs() {
    return this.client.getJSON<IAMPConfigs>(`${this.apiBaseUrl}/api/Settings/amp-config`);
  }

  getSiteMapUrls() {
    return this.client.getJSON<{
      sitemapItems: {
        path: string;
        lastModified: string;
        image?: string;
      }[]
    }>(`${this.apiBaseUrl}/api/Sitemap`, { cache: 'no-store' });
  }

  getAmpSitemapUrls(paging: {page: number; pageSize: number}) {
    return this.client.getJSON<IPaginatedData<{
      path: string;
      lastModified: string;
      image: string;
  }>>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/Sitemap/amp`, paging), { cache: 'no-store' });
  }

  getGeneralSettings() {
    return this.client.getJSON<IGeneralSettings>(`${this.apiBaseUrl}/api/Settings/general`);
  }
  
  
  getSpecialCategory(type: 'life-collection' | 'lifestyle-collection') {
    return this.client.getJSON<ISpecialCategory>(`${this.apiBaseUrl}/api/Posts/collection/${type.split('-')[0]}`);
  }
  

  getEntitySiteMap(entity: 'categories' | 'tags' | 'writers' | 'posts' | 'pages', paging = {page: 1, pageSize: 20000}) {
    return this.client.getJSON<IPaginatedData<{path: string; lastModified: string; image?: string}>>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/Sitemap/${entity}`, paging));
  }

  
  getStories() {
    return this.client.getJSON<IStory[]>(`https://stories.foochia.com/wp-json/web-stories/v1/web-story?_fields=title,link,story_poster`,{}, false);
    
  }

  getCategoryOrTag(
    mode: 'tag' | 'category',
    slug: {
      id: string | number;
      type: 'id' | 'slug';
    },
    paging: {
      page: number;
      pageSize: number;
    } = {
        page: 1,
        pageSize: 10
      },
      extraKeys?: string[]

  ) {
    const params = {
      [slug.type === 'id' ? 'id' : 'key']: decodeURI(String(slug.id)),
      ...paging,
      ...(extraKeys?.length ? {extraKeys} : {})
    }
    return this.client.getJSON<ICategoryOrTag>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/posts/${mode}`, params));
  }


  getDynamnicData(
    slug: string,
    paging: {
      page: number;
      pageSize: number;
    } = {
        page: 1,
        pageSize: 10
      },
      extraKeys?: string[]

  ) {
    return this.client.getJSON<ICategoryOrTag | IStaticPage | ISpecialCategory>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/posts/dynamic/${slug}`, {...paging, ...(extraKeys?.length ? {extraKeys} : {})}));
  }

  getRssFeed(key: string) {
    return this.client.getJSON<{ rssFeedItems: { title: string; description: string; category: string; authors: string[]; url: string; date: string; }[] }>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/rssfeed`, { key }));
  }

  searchPosts(
    filter: Partial<IPostFilter> = {},
    paging: {
      page: number;
      pageSize: number;
    } = {
        page: 1,
        pageSize: 12
      }) {
    const params = {
      ...this.getFormattedPostsFilter(filter),
      ...paging
    };
    return this.client.getJSON<IPaginatedData<IPost>>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/posts/search`, params));
  }


  getSocialFeeds(paging: {
    page: number;
    pageSize: number;
  } = {
      page: 1,
      pageSize: 10
    }) {
    return this.client.getJSON<IPaginatedData<ISocialFeed>>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/SocialFeeds`, paging));

  }

  listCategories() {
    return this.client.getJSON<ICommonCategoryMeta[]>(`${this.apiBaseUrl}/api/Home/list/categories`);
  }


  listStaticPages() {
    return this.client.getJSON<{ id: number; title: string; description: string; body: string; slug: string; type: number; }[]>(`${this.apiBaseUrl}/api/StaticPages`, { next: { revalidate: 3600 } });
  }


  getWriterPage(
    id: string,
    paging: {
      page: number;
      pageSize: number;
    } = {
        page: 1,
        pageSize: 10
      }
  ) {
    const params = {
      writerKey: id,
      ...paging
    }
    return this.client.getJSON<{ writer: IAuthor; postCount: number; posts: IPaginatedData<IPost> }>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/posts/writer`, params));
  }

  getPostById(id: number | string, category: number | string, isAmp = false, previewTags = 4) {
    const url = this.getParameterizedUrl(`${this.apiBaseUrl}/api/posts/${category}/${id}`, {isAmp, previewTags});
    return this.client.getJSON<IPost & { tags?: ICommonCategoryMeta[] }>(url);
  }

  resolvePath(path: string, includePayload = false) {
    const url = this.getParameterizedUrl(`${this.apiBaseUrl}/api/posts/resolve`, {path, includePayload});
    return this.client.getJSON<IResolvePathResponse>(url);
  }

  subscribeToNewsLetter(data: {firstName: string; lastName: string; email: string; allCategories?: boolean, selectedCategorySlugs?: string[]}) {
    return this.client.postJSON<any, typeof data>(`${this.apiBaseUrl}/api/Newsletter/subscribe`, data);
  }

  getNewsLetterSubscription(data: Partial<{token: string; email: string;}>) {
    return this.client.getJSON<INewsLetterSubscription>(`${this.apiBaseUrl}/api/Newsletter/subscriptions?${data.email ? `email=${data.email}` : data.token ? `token=${data.token}` : ''}`);
  }

   updateNewsLetterSubscription(data: {
    "token": string;
    "subscribed": boolean;
    "allCategories": boolean;
    "selectedCategorySlugs": []
  }) {
    return this.client.putJSON<any, typeof data>(`${this.apiBaseUrl}/api/Newsletter/preferences`, data);
  }


  getCategoryMostViewedPosts(
    slug: string,
    pageSize = 5
  ) {
    return this.client.getJSON<IMostViewedPost[]>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/Posts/category/top/${slug}`, {pageSize}));
  }


  listWriters() {
    return this.client.getJSON<{uniqueCode: string; name: string; isUser: boolean;}[]>(`${this.apiBaseUrl}/api/posts/writers`);
  }

  private getFormattedPostsFilter(filter: Partial<INewsFilter> = {}) {
    const { category, fromDate, toDate, q, writer } = filter;
    return {
      ...(category?.length ? { categories: category } : {}),
      ...(writer ? { writer } : {}),
      ...(q ? { keyword: q } : {}),
      ...(fromDate ? { fromDate: new Date(fromDate).toISOString() } : {}),
      ...(toDate ? { toDate: new Date(toDate).toISOString() } : {}),
    }
  }

  listBanners() {
    return this.client.getJSON<IBanner[]>(`${this.apiBaseUrl}/api/Posts/banners/list`);
  }


  listPollCollections(paging: { page: number; pageSize: number; } = { page: 1, pageSize: 10 }) {
      return this.client.getJSON<IPaginatedData<IPollsCollection>>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/polls/collections`, paging));
  }

  getPollCollection(slug: string, deviceId: string) {
      return this.client.getJSON<IPollsCollection>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/polls/collections/${slug}`, {deviceId}), {}, false);
  }


  getPostPoll(id: string, deviceId: string) {
    return this.client.getJSON<IPoll>(this.getParameterizedUrl(`${this.apiBaseUrl}/api/polls/post/${id}`, {deviceId}));
  }

  votePoll(data: {
  "pollUniqueCode": string;
  "deviceId"?: string;
  "answers": {
      "pollQuestionId": number;
      "optionIds"?: number[];
      "answerText"?: string
      "otherAnswer"?: string;
    }[]
}) {
    return this.client.postJSON<IPoll, typeof data>(`${this.apiBaseUrl}/api/polls/vote`, data,{}, false);
  }

  private getParameterizedUrl(url: string, params: { [key: string]: any }) {
    const base = new URL(url);
    const filteredParams = Object.entries(params).filter(([_, val]) => typeof val !== 'undefined');
    for (const [key, val] of filteredParams) {
      if (Array.isArray(val)) {
        for (const item of val) base.searchParams.append(key, item);
      } 
      else base.searchParams.set(key, val);
    }
    return base.href;
  }
}

export const apiService = new ApiService();




